import { css, Interpolation, RuleSet } from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import {
  DirectionKey,
  FontSizeBodyKey,
  FontSizeHeadingKey,
  FontSizes,
  FontWeightKey,
  IHasAlignment,
  IHasColor,
  IHasWidth,
  IHiddenProps,
  ILayoutControl,
  IMAGE_ASPECT_RATIO,
  MediaMapResult,
  ObjectValues,
  TBreakpointKeys,
  THasAlignments,
  XAlignKey
} from '@belong/types';
import { hasValue, isNull, isNumber, isObject, isUndefined } from './value';
import {
  ALIGNMENT,
  BREAKPOINTS,
  COLOURS,
  DIRECTION,
  FLEX_ALIGN,
  FLEX_ALIGN_REVERSE,
  FOCUS_RING_WIDTH,
  FONT_NAME,
  FONT_WEIGHT
} from './variables';

export const convertWeight: Record<FontWeightKey, string> = {
  BOLD: 'Bold',
  SEMI_BOLD: 'Semibold',
  REGULAR: 'Regular'
};

export const FONT_VARIANTS = [
  { ext: 'woff2', type: 'woff2' },
  { ext: 'woff', type: 'woff' },
  { ext: 'ttf', type: 'truetype' }
];

const FALLBACK_FONT_NAMES: Record<FontWeightKey, string[]> = {
  BOLD: ['Arial-Bold', 'Arial-BoldMT'],
  SEMI_BOLD: ['Arial-Bold', 'Arial-BoldMT'],
  REGULAR: ['Arial']
};

const FALLBACK_FONT_ADJUSTMENTS: Record<FontWeightKey, string> = {
  BOLD: 'ascent-override: 90%; descent-override: 26.7%; line-gap-override: 44.7%; size-adjust: 94.5%;',
  SEMI_BOLD: 'ascent-override: 90%; descent-override: 26.7%; line-gap-override: 44.7%; size-adjust: 94.5%;',
  REGULAR: 'ascent-override: 90%; descent-override: 26.7%; line-gap-override: 44.7%;'
};

export const fontFace = (
  staticFolder: string,
  name: ObjectValues<typeof FONT_NAME>,
  weight: FontWeightKey
): RuleSet => {
  const fontBase = `${staticFolder}/fonts/${name}-${convertWeight[weight]}`;
  return css`
    @font-face {
      font-family: ${name};
      font-style: normal;
      font-display: swap;
      font-weight: ${FONT_WEIGHT[weight]};
      src: ${FONT_VARIANTS.map(({ ext, type }) => `url('${fontBase}.${ext}') format('${type}')`).join(',')};
    }
  `;
};

export const fallbackFontFace = (name: ObjectValues<typeof FONT_NAME>, weight: FontWeightKey): RuleSet => {
  return css`
    @font-face {
      font-family: ${name};
      font-weight: ${FONT_WEIGHT[weight]};
      font-style: normal;
      src: ${FALLBACK_FONT_NAMES[weight].map(fontName => `local('${fontName}')`).join(',')};
      ${FALLBACK_FONT_ADJUSTMENTS[weight]}
    }
  `;
};

export const spacingMixin = ({ hasMargin, hasPadding }: any): any => css`
  ${hasMargin && !isObject(hasMargin)
    ? `margin: ${toCSSUnits(hasMargin)}`
    : mediaMap(
        hasMargin,
        (margin: any) => css`
          margin: ${toCSSUnits(margin)};
        `
      )};
  ${hasPadding && !isObject(hasPadding)
    ? `padding: ${toCSSUnits(hasPadding)}`
    : mediaMap(
        hasPadding,
        (padding: any) => css`
          padding: ${toCSSUnits(padding)};
        `
      )};
`;

/**
 * convert number/string to rem
 * */
const toCSSUnits = (value: any, unit = 'rem'): string => {
  if (isNumber(value)) {
    return value.toString() + unit;
  }

  return value;
};

type MediaExtensionFn = (cssString: string | TemplateStringsArray, ...args: any[]) => any;

export type MediaExtensions = Record<TBreakpointKeys, MediaExtensionFn>;

type MediaFn = (gte: TBreakpointKeys, lte?: TBreakpointKeys) => any;

export const media: MediaFn & MediaExtensions = breakpoint as MediaFn & MediaExtensions;

export const smallerThan = (breakpoint1: TBreakpointKeys, breakpoint2: TBreakpointKeys): boolean => {
  const breakpointKeys = ['xs', 'sm', 'md', 'lg', 'xl'];
  const bt1Idx = breakpointKeys.indexOf(breakpoint1);
  const bt2Idx = breakpointKeys.indexOf(breakpoint2);
  return bt1Idx < bt2Idx;
};

/**
 * Helper method to generate css for multiple breakpoints
 * @param {Object} values - object containing breakpoint data e.g. { sm: 300, lg: 500 }
 * @param {(valueProp) => css``} callback - function to output css for a given breakpoint
 * @return Styled CSS String
 *
 * @example
 * set width of div based on breakpoint
 * const getSizeProp = (props) => ({ size: props.size });
 * const Wrapper = styled.div.attrs(getSizeProp)`
 *  ${({ size }) => mediaMap(size, (size) => `width: ${size}px`;)}
 * `;
 *
 */
export const mediaMap = (values: any, callback?: any): MediaMapResult => {
  if (isUndefined(values) || isNull(values)) {
    return;
  }

  const results: any[] = [];

  if (isObject(values)) {
    Object.keys(values).forEach(key => {
      const value = values[key];
      if (hasValue(value) && key in BREAKPOINTS) {
        results.push(media(key as TBreakpointKeys)`${callback(value, key)}`);
      }
    });
  } else {
    results.push(callback(values));
  }

  if (results.length === 0) {
    return;
  }

  return results;
};

const getWidthCssString = (width: string): RuleSet => {
  switch (width) {
    case 'small':
      return css`
        width: 8rem;
      `;
    case 'medium':
      return css`
        width: 14.8rem;
      `;
    case 'large':
      return css`
        width: 19.4rem;
      `;
    case 'half':
      return css`
        width: 50%;
      `;
    case 'full':
      return css`
        width: 100%;
      `;
    default:
      return css`
        width: 100%;
      `;
  }
};

export const width: Interpolation<IHasWidth> = (props: IHasWidth) => mediaMap(props.width, getWidthCssString);

export const hasAlignment: Interpolation<IHasAlignment> = props =>
  mediaMap(
    props.alignment,
    (alignment: THasAlignments) => css`
      text-align: ${alignment};
    `
  );

export const hasFlexAlignment: Interpolation<IHasAlignment> = props =>
  mediaMap(
    props.alignment,
    (alignment: XAlignKey) => css`
      justify-content: ${FLEX_ALIGN[alignment]};
    `
  );

export const hasFlexAlignmentItems: Interpolation<IHasAlignment> = props =>
  mediaMap(
    props.alignment,
    (alignment: XAlignKey) => css`
      align-items: ${FLEX_ALIGN[alignment]};
    `
  );

export const hasColor: Interpolation<IHasColor> = props => css`
  color: ${props.hasColor};
`;

export const isHidden: Interpolation<IHiddenProps> = props =>
  props.isHidden &&
  media(props.isHidden[0], props.isHidden[1])`
    display: none !important;
  `;

export const isFocused = (inset = false, outlineOffset = FOCUS_RING_WIDTH): RuleSet => css`
  outline: ${FOCUS_RING_WIDTH}rem solid ${COLOURS.BELONG_BLUE_ALT};
  outline-offset: ${inset ? '-0.4' : outlineOffset}rem;
`;

export const applyFlexLayout = ({ hasControl }: ILayoutControl, margin = '1.6rem'): RuleSet => css`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  ${mediaMap(hasControl, (config: [DirectionKey, XAlignKey, boolean]) => {
    const [direction = DIRECTION.row, align = ALIGNMENT.left, reverse = false] = config;

    switch (direction) {
      case 'row':
        return css`
          flex-direction: ${reverse ? 'row-reverse' : 'row'};
          align-items: ${reverse ? 'flex-end' : 'flex-start'};
          justify-content: ${reverse ? FLEX_ALIGN_REVERSE[align] : FLEX_ALIGN[align]};
          column-gap: ${margin};
        `;
      case 'column':
        return css`
          flex-direction: ${reverse ? 'column-reverse' : 'column'};
          justify-content: ${reverse ? 'flex-start' : 'flex-end'};
          align-items: ${FLEX_ALIGN[align]};
          row-gap: ${margin};
        `;

      default:
        return css``;
    }
  })}
`;

export const textShimmer = css`
  background: linear-gradient(230deg, #f4f4f4, #99b1ca);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  [data-platform*='Win'] & {
    padding-bottom: 1rem; // bounding box of the text doesn't extend beyond the baseline on windows so it cuts off the descenders
    margin-bottom: -1rem;
  }
`;

export const wh = (ratio: string): string[] => ratio.split(':');

export const getAspectRatio = (ratio: string): string => {
  if (ratio === IMAGE_ASPECT_RATIO.auto) {
    return ratio;
  }

  const [w = 16, h = 9] = wh(ratio);
  return `${w} / ${h}`;
};

export const getPaddingTop = (ratio: string): string => {
  const [w = 16, h = 9] = wh(ratio);
  return `calc(${h} / ${w} * 100%);`;
};

export const grayScale = css`
  -webkit-filter: grayscale(100%);
  filter: grayscale(100%);
`;

export const getFont = (fontSize: FontSizes): RuleSet => css`
  font-size: ${fontSize};
  line-height: ${({ theme }): string => theme.fonts.base[fontSize].lineHeight};
  letter-spacing: ${({ theme }): string => theme.fonts.base[fontSize].letterSpacing};
`;

export const getResponsiveFont = (fontXs: FontSizes, fontMd: FontSizes): RuleSet => css`
  ${getFont(fontXs)};

  /* Only add responsive size if it's different */
  ${fontXs !== fontMd ? media('md')`${getFont(fontMd)}` : ''}
`;

export const getBodyFont = (size: FontSizeBodyKey): RuleSet => css`
  ${({ theme }): RuleSet => getResponsiveFont(theme.fonts.fontSize.body.xs[size], theme.fonts.fontSize.body.md[size])}
`;

export const getHeadingFont = (size: FontSizeHeadingKey): RuleSet => css`
  ${({ theme }): RuleSet =>
    getResponsiveFont(theme.fonts.fontSize.heading.xs[size], theme.fonts.fontSize.heading.md[size])}
`;
